import { useCallback, useEffect, useState } from 'react';
import { showtoast } from '../../../../../../../../../util/toast';
import RetailerApiRequestModel from '../../../../../../../../models/RetailerApiRequestModel';
import { emptyRetailerAuthMethodsDraft } from '../../components/toolModalViews/RetailerAuthMethodsSection';
import {
  draftFromRetailerApiRequestArgs,
  emptyRetailerApiRequestDraft,
  RETAILER_AUTH_METHOD_NONE_ID,
  tryBuildRetailerApiRequestPayload
} from './retailerApiRequestPayload';

const useRetailerApiRequestTool = ({
  t,
  mode,
  onSelectTool,
  selectedTool,
  closeAction,
  initialTool,
  initialArgs,
  appliedInitialArgsRef,
  resetAppliedInitialArgs
}) => {
  const [apiRequestDraft, setApiRequestDraft] = useState(emptyRetailerApiRequestDraft);
  const [authMethodsDraft, setAuthMethodsDraft] = useState(emptyRetailerAuthMethodsDraft);
  const [selectedAuthMethodId, setSelectedAuthMethodId] = useState(RETAILER_AUTH_METHOD_NONE_ID);
  const [authMethodsFormMode, setAuthMethodsFormMode] = useState('select');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [requestTestPassed, setRequestTestPassed] = useState(false);

  const invalidateRequestTest = useCallback(() => {
    setTestResult(null);
    setRequestTestPassed(false);
  }, []);

  const reset = useCallback(() => {
    setApiRequestDraft(emptyRetailerApiRequestDraft());
    setAuthMethodsDraft(emptyRetailerAuthMethodsDraft());
    setSelectedAuthMethodId(RETAILER_AUTH_METHOD_NONE_ID);
    setAuthMethodsFormMode('select');
    setTestResult(null);
    setRequestTestPassed(false);
    resetAppliedInitialArgs();
  }, [resetAppliedInitialArgs]);

  const patchDraft = useCallback((partial) => {
    setApiRequestDraft((prev) => ({ ...prev, ...partial }));
    invalidateRequestTest();
  }, [invalidateRequestTest]);

  const updateSelectedAuthMethodId = useCallback((authMethodId) => {
    setSelectedAuthMethodId(authMethodId);
    invalidateRequestTest();
  }, [invalidateRequestTest]);

  const updateAuthMethodsFormMode = useCallback((formMode) => {
    setAuthMethodsFormMode(formMode);
    invalidateRequestTest();
  }, [invalidateRequestTest]);

  useEffect(() => {
    if (initialTool?.id !== 'retailer_api_request') return;
    if (!initialArgs?.length || appliedInitialArgsRef.current) return;

    const { draft, authMethodId } = draftFromRetailerApiRequestArgs(initialArgs);
    setApiRequestDraft(draft);
    setSelectedAuthMethodId(authMethodId);
    setRequestTestPassed(true);
    appliedInitialArgsRef.current = true;
  }, [appliedInitialArgsRef, initialArgs, initialTool]);

  const buildPayload = useCallback(() => tryBuildRetailerApiRequestPayload({
    ...apiRequestDraft,
    authMethodId: selectedAuthMethodId
  }), [apiRequestDraft, selectedAuthMethodId]);

  const handleConfirm = useCallback(() => {
    if (mode === 'browse' || !onSelectTool || !selectedTool) return;

    const result = buildPayload();
    if (!result.ok) {
      showtoast(t(result.errorKey));
      return;
    }

    if (!requestTestPassed) {
      showtoast(t('tools.retailer_api_request_errors.request_not_tested'));
      return;
    }

    onSelectTool({ ...selectedTool, name: selectedTool.id, args: [result.payload] });
    closeAction();
  }, [buildPayload, closeAction, mode, onSelectTool, requestTestPassed, selectedTool, t]);

  const handleTest = useCallback(async () => {
    if (authMethodsFormMode === 'create') {
      showtoast(t('tools.retailer_api_request_test_errors.auth_not_saved'));
      return;
    }

    const result = buildPayload();
    if (!result.ok) {
      showtoast(t(result.errorKey));
      return;
    }

    setTesting(true);
    invalidateRequestTest();

    try {
      const response = await RetailerApiRequestModel.test(result.payload);
      setTestResult(response);
      setRequestTestPassed(Boolean(response?.success));
    } catch (error) {
      setTestResult({
        success: false,
        message: error?.message || t('tools.retailer_api_request_test_errors.request_failed')
      });
      setRequestTestPassed(false);
    } finally {
      setTesting(false);
    }
  }, [authMethodsFormMode, buildPayload, invalidateRequestTest, t]);

  return {
    reset,
    handleConfirm,
    handleTest,
    testing,
    testResult,
    apiRequestDraft,
    patchDraft,
    authMethodsDraft,
    setAuthMethodsDraft,
    selectedAuthMethodId,
    setSelectedAuthMethodId: updateSelectedAuthMethodId,
    authMethodsFormMode,
    setAuthMethodsFormMode: updateAuthMethodsFormMode
  };
};

export default useRetailerApiRequestTool;
